// Button primitive. Follows shadcn/ui's Slot pattern but ships as plain JSX
// (no shadcn CLI). Variants honor DESIGN.md's color discipline:
// - primary: Terracotta surface (the one-voice rule moment)
// - ghost: Sandstone hover bg, no terracotta
// - destructive: iron-rust outline + text (for Remove actions)

import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { forwardRef } from 'react'
import { cn } from '../../lib/utils.js'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-terracotta)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-limestone)] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-terracotta)] text-[var(--color-limestone)] hover:bg-[var(--color-terracotta-deep)]',
        ghost: 'bg-transparent text-[var(--color-basalt)] hover:bg-[var(--color-sandstone)]',
        outline:
          'border border-[var(--color-sandstone)] bg-transparent text-[var(--color-basalt)] hover:bg-[var(--color-sandstone)]',
        destructive:
          'border border-[var(--color-failed)] bg-transparent text-[var(--color-failed)] hover:bg-[var(--color-failed)]/10',
        link: 'bg-transparent text-[var(--color-terracotta)] hover:underline underline-offset-2 p-0 h-auto',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export const Button = forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
})
Button.displayName = 'Button'

export { buttonVariants }
